import { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronDown,
  ChevronRight,
  BarChart3,
  ExternalLink,
  BookOpen,
  Headphones,
} from "lucide-react";
import "../../styles/Help.css";
import "../../styles/attendanceHelp.css";
import "../../styles/supportCenter.css";

const QUICK_ACTIONS = [
  { label: "Payroll Reports", path: "/reports/payroll", desc: "Payroll Register, Salary Register, Bank Transfer, LOP" },
  { label: "Employee Reports", path: "/reports/employees", desc: "Employee Master, Directory, Joining, Exit reports" },
  { label: "Attendance Reports", path: "/reports/attendance", desc: "Daily, Monthly, Absent, Late Coming, Register" },
  { label: "Leave Reports", path: "/reports/leave", desc: "Leave Balance, Register, Utilization, Pending Requests" },
  { label: "Statutory Reports", path: "/reports/statutory", desc: "PF, ESI, and TDS compliance reports" },
  { label: "ESI Reports", path: "/reports/esi", desc: "Dedicated ESI contribution workspace" },
  { label: "Salary Register", path: "/reports/payroll/employee-salary-register", desc: "Full monthly breakup per employee from payroll snapshots" },
];

const REPORTS_TROUBLESHOOTING = [
  {
    problem: "Report shows 'No records found' after generating",
    check: "The report filter selection does not match any existing data. For payroll reports, data is only available from processed payroll runs. For statutory reports, data comes only from finalized (locked) payroll runs. For attendance and leave reports, records must exist for the selected date range.",
    action: "Adjust your filters — try a different payroll month, broaden the department filter, or expand the date range. For payroll reports, verify that payroll has been processed for the selected month. For statutory reports, ensure the payroll run is finalized.",
    path: "/reports/payroll",
    pathLabel: "Payroll Reports",
  },
  {
    problem: "Cannot export — 'Generate the report before exporting'",
    check: "Report data must be loaded before it can be exported or printed. The export and print buttons are only active after clicking Generate to load the report results.",
    action: "Set your desired filters, click the Generate button to load the report data, and then use the export buttons (Excel, CSV, PDF, or Print) from the toolbar.",
    path: "/reports/payroll",
    pathLabel: "Payroll Reports",
  },
  {
    problem: "PDF export shows 'PDF export is not available yet' for Employee reports",
    check: "PDF export is not currently implemented for Employee reports (Employee Master, Employee Directory, Joining Report, Exit Report). These reports support View (modal preview) and Excel export only.",
    action: "Use the Excel export option instead. PDF export is available for Attendance, Leave, Payroll, Statutory, and ESI reports.",
    path: "/reports/employees",
    pathLabel: "Employee Reports",
  },
  {
    problem: "Export failed or produced an empty file",
    check: "Export requires the report to contain data rows. If the report shows 'No records found', the export will fail or produce an empty file. Also verify that the selected format is supported for the specific report type.",
    action: "Adjust your filters to include data, regenerate the report to confirm rows are displayed, then retry the export. Check which formats the report supports — Employee reports support Excel only, while Leave and Payroll reports support Excel, CSV, PDF, and Print.",
    path: "/reports/payroll",
    pathLabel: "Payroll Reports",
  },
  {
    problem: "Employee Salary Register export fails — 'Ensure a payroll run is processed'",
    check: "The Employee Salary Register reads data from frozen payroll snapshots. If no payroll run has been processed for the selected period, there is no data to display or export.",
    action: "Process payroll for the desired month first, then return to the Employee Salary Register, select that payroll run from the filter, and retry. You can also filter by Financial Year, Department, or search for specific employees.",
    path: "/reports/payroll/employee-salary-register",
    pathLabel: "Salary Register",
  },
  {
    problem: "Statutory report shows no rows — 'Reports read finalized payroll runs only'",
    check: "Statutory reports (PF, ESI, TDS) require the payroll run to be finalized (locked). A processed but unlocked payroll run will not appear in statutory reports.",
    action: "Go to Payroll and finalize (lock) the payroll run for the selected period. Then return to the Statutory Reports workspace and regenerate the report.",
    path: "/reports/statutory",
    pathLabel: "Statutory Reports",
  },
  {
    problem: "ESI report shows 'No employees match the selected filters'",
    check: "The ESI report has extensive filters including Period, Month, Year, Department, Designation, Location, Status, and ESI Eligible. If your filter combination is too restrictive, no employees will match.",
    action: "Broaden your filter selection — try removing department, designation, or status filters. Ensure that employees have ESI applicable set to true in their statutory details. Verify the selected period contains finalized payroll data.",
    path: "/reports/esi",
    pathLabel: "ESI Reports",
  },
  {
    problem: "TDS report requires payroll month but none are available in the dropdown",
    check: "The TDS report Payroll Month dropdown shows only processed payroll runs. If no payroll has been processed, the dropdown will be empty.",
    action: "Process payroll for the desired month, then return to the TDS report. Select the payroll run from the Payroll Month dropdown and generate the report.",
    path: "/reports/payroll",
    pathLabel: "Payroll Reports",
  },
  {
    problem: "Report data does not match expectations — amounts or counts seem wrong",
    check: "Report data is generated from the underlying module data (employee records, attendance, leave transactions, payroll runs). If the source data is incomplete or incorrect, the report will reflect those issues. Payroll reports pull from the most recent processed payroll run for the selected month.",
    action: "Verify the source data first — check employee records, attendance, leave balances, or payroll processing for the relevant period. Correct any issues in the source module, then regenerate the report.",
    path: "/reports",
    pathLabel: "Reports",
  },
  {
    problem: "Report columns are missing or hidden",
    check: "Leave, Payroll, and ESI reports have a column visibility toggle — a checkbox dropdown that lets you show or hide specific columns. If columns appear missing, they may have been hidden via this toggle.",
    action: "Look for the column visibility dropdown in the report toolbar. Click it and ensure all desired columns are checked. The setting is stored per session.",
    path: "/reports/payroll",
    pathLabel: "Payroll Reports",
  },
  {
    problem: "Cannot access Reports — page redirects or shows access denied",
    check: "The Reports module is available to Admin and HR roles. The Employee Salary Register, Statutory Reports, and ESI Reports are also accessible to the Finance role. Employees do not have access to the Reports module.",
    action: "Check your role by going to My Profile. If you need access to reports, contact your organisation's admin to update your role to admin, hr, or finance.",
    path: "/profile",
    pathLabel: "My Profile",
  },
];

export default function ReportsSupportPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [openItem, setOpenItem] = useState(null);
  const resultsRef = useRef(null);

  const q = search.trim().toLowerCase();

  const filteredItems = useMemo(() => {
    if (!q) return REPORTS_TROUBLESHOOTING;
    return REPORTS_TROUBLESHOOTING.filter(
      (t) =>
        t.problem.toLowerCase().includes(q) ||
        t.check.toLowerCase().includes(q) ||
        t.action.toLowerCase().includes(q)
    );
  }, [q]);

  return (
    <div className="brix-page">
      {/* ── Header ── */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <div className="ah-hero-title-row">
            <div className="ah-hero-icon ic-indigo">
              <BarChart3 size={28} strokeWidth={1.8} />
            </div>
            <div>
              <h1>Reports Issues</h1>
              <p className="hs-hero-sub">
                Troubleshoot report generation, filtering, and data export issues.
              </p>
            </div>
          </div>

          <form
            className="hs-search-wrap"
            onSubmit={(e) => {
              e.preventDefault();
              if (q && resultsRef.current) resultsRef.current.scrollIntoView({ behavior: "smooth" });
            }}
          >
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input
                type="text"
                className="hs-search-input"
                placeholder="Search report issues — e.g. export, PDF, statutory, empty..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>
        </div>
      </div>

      <div ref={resultsRef}>
        {/* ── Quick Actions ── */}
        <div style={{ marginBottom: 28 }}>
          <div className="hs-section-head">
            <h2 className="hs-section-title">Quick Actions</h2>
            <p className="hs-section-sub">Jump to the relevant report page to check or fix your issue</p>
          </div>
          <div className="sc-issues-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
            {QUICK_ACTIONS.map((link) => (
              <div
                key={link.path}
                className="sc-issue-card"
                role="button"
                tabIndex={0}
                style={{ cursor: "pointer" }}
                onClick={() => navigate(link.path)}
                onKeyDown={(e) => e.key === "Enter" && navigate(link.path)}
              >
                <div className="sc-issue-name" style={{ fontSize: "0.88rem" }}>{link.label}</div>
                <div className="sc-issue-desc" style={{ fontSize: "0.78rem" }}>{link.desc}</div>
                <div className="sc-issue-link">
                  Open <ExternalLink size={12} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Report Troubleshooting (main section) ── */}
        <div style={{ marginBottom: 28 }}>
          <div className="hs-section-head">
            <h2 className="hs-section-title">Report Troubleshooting</h2>
            <p className="hs-section-sub">
              Select your issue to see what to check and how to resolve it
            </p>
          </div>

          {q && filteredItems.length === 0 && (
            <div className="sc-no-results">
              No report issues match &ldquo;<strong>{search}</strong>&rdquo;. Try a different keyword.
            </div>
          )}

          <div className="sc-accordion-list">
            {filteredItems.map((item, idx) => {
              const isOpen = openItem === idx;
              return (
                <div key={item.problem} className="hs-faq-item">
                  <button
                    type="button"
                    className="hs-faq-btn"
                    onClick={() => setOpenItem(isOpen ? null : idx)}
                  >
                    <span className="hs-faq-q">{item.problem}</span>
                    <ChevronDown size={16} className={`hs-faq-chevron${isOpen ? " open" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="hs-faq-answer">
                          <div style={{ marginBottom: 10 }}>
                            <strong style={{ color: "#64748b", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Check
                            </strong>
                            <p style={{ margin: "4px 0 0" }}>{item.check}</p>
                          </div>
                          <div style={{ marginBottom: item.path ? 12 : 0 }}>
                            <strong style={{ color: "#15803d", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Next Action
                            </strong>
                            <p style={{ margin: "4px 0 0" }}>{item.action}</p>
                          </div>
                          {item.path && (
                            <button
                              type="button"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "6px 14px",
                                fontSize: "0.82rem",
                                fontWeight: 500,
                                color: "#1a437d",
                                background: "rgba(26,67,125,0.06)",
                                border: "1px solid rgba(26,67,125,0.15)",
                                borderRadius: 6,
                                cursor: "pointer",
                              }}
                              onClick={() => navigate(item.path)}
                            >
                              {item.pathLabel}
                              <ExternalLink size={13} />
                            </button>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Need detailed report information? ── */}
        <div className="sc-col-card" style={{ marginBottom: 28 }}>
          <div className="sc-info-row">
            <div className="sc-col-header-icon sc-icon-blue">
              <BookOpen size={18} strokeWidth={2} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 className="hs-section-title">Need detailed report information?</h2>
              <p className="sc-info-row-desc">
                Visit the Reports Help Guide for detailed documentation, FAQs and step-by-step report instructions.
              </p>
            </div>
            <button
              type="button"
              className="sc-info-row-btn"
              onClick={() => navigate("/help/reports")}
            >
              Open Reports Help Guide
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* ── Still facing an issue? ── */}
        <div className="sc-contact-card" style={{ marginBottom: 28 }}>
          <div className="sc-contact-inner sc-contact-row">
            <div className="sc-contact-icon">
              <Headphones size={24} strokeWidth={1.5} />
            </div>
            <div className="sc-contact-text">
              <h2 className="sc-contact-title">Still facing an issue?</h2>
              <p className="sc-contact-sub">
                If the available troubleshooting steps don't resolve the problem, contact our support team for further assistance.
              </p>
            </div>
            <button
              type="button"
              className="sc-contact-btn"
              onClick={() => navigate("/support")}
            >
              <Headphones size={15} />
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
